import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@/lib/ThemeContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import MobileStickyCTA from '@/components/layout/MobileStickyCTA';
import HomePage from '@/pages/HomePage';
import ServicesPage from '@/pages/ServicesPage';
import ContactPage from '@/pages/ContactPage';
import LandingPage from '@/pages/LandingPage';
import FAQPage from '@/pages/FAQPage';
import TrainingPage from '@/pages/TrainingPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import NotFoundPage from '@/pages/NotFoundPage';
import LandingThankYouPage from '@/pages/LandingThankYouPage';
import { trackEvent } from '@/lib/capi';
import { stripTrailingSlash } from '@/lib/utils';
import '@/App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_location: window.location.href,
      });
    }
    // The head snippet already fires the pixel's initial PageView (and its CAPI
    // mirror) on document load, so only fire here on an actual pathname change —
    // comparing against the previous pathname (rather than a "have I run yet" flag)
    // keeps this idempotent under StrictMode's dev-only double-invoke of effects,
    // which would otherwise replay a flag flip and double-count the first page.
    if (prevPathname.current !== pathname) {
      trackEvent('PageView');
    }
    prevPathname.current = pathname;
  }, [pathname]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const hideChrome = stripTrailingSlash(pathname).startsWith('/ai-receptionist-for-tradies');

  return (
    <div className="min-h-screen bg-brand-black text-brand-white font-sans">
      {!hideChrome && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* About Page blocked out until redesigned. */}
          {/* <Route path="/about" element={<AboutPage />} /> */}
          <Route path="/services" element={<ServicesPage />} />
          {/* <Route path="/chat" element={<AIChatPage />} /> */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/training-workshop" element={<TrainingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Tradie landing page (indexed); /landing 301s here for existing ad links. Not in the navbar. */}
          <Route path="/ai-receptionist-for-tradies" element={<LandingPage />} />
          {/* Post-submit page for the landing form; its URL is the conversion counter. Noindexed, not in the sitemap. */}
          <Route path="/ai-receptionist-for-tradies/thank-you" element={<LandingThankYouPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <MobileStickyCTA />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppShell />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
